import React from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { FormikProvider } from 'formik'
import {
  ContactEditPageDocument,
  ContactForFinancialDetailFragment,
  FinanceValueObjectsLedgerAccountOperationTypeEnum,
  LedgerAccountResourceForContactFragment,
  LegalEntityForFinancialDetailFragment,
  UpsertLedgerAccountResourceDocument,
} from 'generated/resources/graphql'

import { useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import LedgerAccountSelectorItem from './LedgerAccountSelectorItem'

interface LedgerAccountFormValues {
  contact?: ContactForFinancialDetailFragment
  accountResourcesForLegalEntity: {
    legalEntityId: number
    accountResources: LedgerAccountResourceForContactFragment[]
  }[]
}

const defaultAccountResource = (
  operationType: FinanceValueObjectsLedgerAccountOperationTypeEnum = 'purchase'
): LedgerAccountResourceForContactFragment => ({
  id: -1,
  account: {
    id: 0,
    name: '',
    number: '',
    legalEntityId: 0,
    type: 'non_current_asset',
  },
  operationType: operationType,
})

const mapExistingAccountResources = (
  seeSalesLedgerAccounts: boolean,
  seePurchaseLedgerAccounts: boolean,
  contact?: ContactForFinancialDetailFragment
) =>
  Object.entries(
    contact?.accountResources?.reduce(
      (acc, accountResource) => {
        const legalEntityId = accountResource.account.legalEntityId
        if (!legalEntityId) return acc

        if (!acc[legalEntityId]) {
          acc[legalEntityId] = []
        }
        acc[legalEntityId].push(accountResource)

        return acc
      },
      {} as Record<number, LedgerAccountResourceForContactFragment[]>
    ) || {}
  ).map(([legalEntityId, legalEntityAccountResources]) => {
    const resources = fillAccountResources(
      seePurchaseLedgerAccounts,
      seeSalesLedgerAccounts,
      legalEntityAccountResources
    )
    return { legalEntityId: Number(legalEntityId), accountResources: resources }
  })

const accountResourceToAdd = (
  accountResources: LedgerAccountResourceForContactFragment[],
  operationType: FinanceValueObjectsLedgerAccountOperationTypeEnum | undefined
) => {
  const matchingAccountResource = accountResources.find(
    (accountResource) => accountResource.operationType == operationType
  )
  if (matchingAccountResource) {
    return matchingAccountResource
  } else {
    return defaultAccountResource(operationType)
  }
}

const fillAccountResources = (
  seePurchaseLedgerAccounts: boolean,
  seeSalesLedgerAccounts: boolean,
  accountResources: LedgerAccountResourceForContactFragment[]
) => {
  const results: LedgerAccountResourceForContactFragment[] = []
  if (seePurchaseLedgerAccounts) {
    // we need to make sure purchase one goes first
    results.unshift(accountResourceToAdd(accountResources, 'purchase'))
  }

  if (seeSalesLedgerAccounts) {
    results.push(accountResourceToAdd(accountResources, 'sale'))
  }
  return results
}

interface LedgerAccountSelectorProps {
  legalEntities: LegalEntityForFinancialDetailFragment[]
  contact?: ContactForFinancialDetailFragment
  canManage: boolean
  seeSalesLedgerAccounts?: boolean
  seePurchaseLedgerAccounts?: boolean
}

const LedgerAccountSelectorForm: React.FC<LedgerAccountSelectorProps> = ({
  legalEntities,
  contact,
  canManage,
  seeSalesLedgerAccounts = false,
  seePurchaseLedgerAccounts = false,
}) => {
  const [upsertLedgerAccountResource] = useMutation(UpsertLedgerAccountResourceDocument, {
    extractorFn: (data) => data.financeMutations.upsertFinanceLedgerAccountResource,
    awaitRefetchQueries: true,
    refetchQueries: [ContactEditPageDocument],
  })

  const defaultAccountResources = () => {
    return fillAccountResources(seePurchaseLedgerAccounts, seeSalesLedgerAccounts, [])
  }

  const existingAccountResourcesForLegalEntity = mapExistingAccountResources(
    seeSalesLedgerAccounts,
    seePurchaseLedgerAccounts,
    contact
  )

  const form = useForm<LedgerAccountFormValues>({
    enableReinitialize: true,
    defaultValues: {
      accountResourcesForLegalEntity: existingAccountResourcesForLegalEntity.length
        ? existingAccountResourcesForLegalEntity
        : [
            {
              legalEntityId: -1,
              accountResources: defaultAccountResources(),
            },
          ],
      contact,
    },
    onSubmit: async (data) => {
      await Promise.all(
        data.accountResourcesForLegalEntity.flatMap(({ accountResources }) =>
          accountResources.map(({ id, account, operationType }) =>
            account.id
              ? upsertLedgerAccountResource({
                  variables: {
                    id: id > 0 ? id : undefined,
                    legalEntityId: account.legalEntityId ?? 0,
                    accountId: account.id,
                    resourceId: contact?.id,
                    resourceType: 'vendor',
                    operationType: operationType,
                  },
                })
              : null
          )
        )
      )
      app.setFlash({ type: 'correct', message: i18n.t('common.saved_successfully'), time: 2000 })
      return { success: true, data: null }
    },
  })

  const handleAddRow = () => {
    form.setFieldValue('accountResourcesForLegalEntity', [
      ...form.values.accountResourcesForLegalEntity,
      {
        legalEntityId: (form.values.accountResourcesForLegalEntity.length + 1) * -1,
        accountResources: defaultAccountResources(),
      },
    ])
  }

  const showAdd = legalEntities.length != form.values.accountResourcesForLegalEntity?.length

  const allLegalEntityIdsUsed = form.values.accountResourcesForLegalEntity.map(
    (ar) => ar.legalEntityId
  )
  const getAvailableLegalEntities = (legalEntityId: number) =>
    legalEntities.filter((le) => le.id === legalEntityId || !allLegalEntityIdsUsed.includes(le.id))

  return (
    <FormikProvider value={form}>
      {form.values.accountResourcesForLegalEntity.map((accountResourceForLegalEntity, index) => (
        <Box key={index} flexDirection="row">
          <LedgerAccountSelectorItem
            formFieldName={`accountResourcesForLegalEntity.${index}`}
            legalEntities={getAvailableLegalEntities(accountResourceForLegalEntity.legalEntityId)}
            submitting={form.isSubmitting}
            canManage={canManage}
            seeSalesLedgerAccounts={seeSalesLedgerAccounts}
            seePurchaseLedgerAccounts={seePurchaseLedgerAccounts}
          />
        </Box>
      ))}

      <Box
        flexDirection="row"
        width="full"
        gap="s16"
        alignItems="flexEnd"
        alignSelf="flexEnd"
        justifyContent="flexEnd"
      >
        {showAdd && (
          <Box alignItems="flexStart" width="full">
            <Button
              onClick={handleAddRow}
              disabled={form.isSubmitting}
              type="secondary"
              iconName="add"
            >
              {i18n.t('finance.contacts.contact_details.ledger_account.add')}
            </Button>
          </Box>
        )}
        {form.dirty && (
          <Box alignItems="flexEnd" alignSelf="flexEnd" justifyContent="flexEnd">
            <Button onClick={() => form.submitForm()} disabled={form.isSubmitting}>
              {i18n.t('common.save')}
            </Button>
          </Box>
        )}
      </Box>
    </FormikProvider>
  )
}
export default LedgerAccountSelectorForm
